import { Accounts, Consumers, Monitors, Planning, Team } from 'views';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SupplierInfo } from 'types';
import Box from '@mui/material/Box';
import TabContext from '@mui/lab/TabContext';
import { Typography } from '@mui/material';
import {
    Assignment,
    Business,
    Insights,
    Leaderboard,
    Map,
    People as PeopleIcon,
    PropaneTank,
    Speed,
    Today,
} from '@mui/icons-material';
import { getSupplierInfo } from 'store/services/supplier';
import { Organization } from 'views/Organization/Organization';
import styles from './SupplierDetails.module.scss';
import { LiveMonitoring } from 'views/LiveMonitoring/LiveMonitoring';
import { Efficiency } from 'views/Efficiency/Efficiency';
import { Issues } from 'views/Issues/Issues';
import { setViewAsSupplier } from 'store/features/viewAsSupplier';
import { useDispatch } from 'react-redux';
import { DeviceMap } from 'components/data/DeviceMap/DeviceMap';
import { TuTabList } from 'components/common/TuTabList/TuTabList';
import { TuTab } from 'components/common/TuTab/TuTab';
import { TuTabPanel } from 'components/common/TuTabPanel/TuTabPanel';

export const SupplierDetails = () => {
    const params = useParams();
    const dispatch = useDispatch();

    const [supplierInfo, setSupplierInfo] = useState<SupplierInfo | undefined>(
        undefined
    );
    const [supplierId, setSupplierId] = useState<string | undefined>(undefined);

    const [value, setValue] = React.useState(
        params.supplierTabName ?? 'accounts'
    );

    const handleChange = (_: React.SyntheticEvent, newValue: string) => {
        setValue(newValue);
        window.history.replaceState(
            null,
            '',
            `/supplier-details/${supplierId}/${newValue}`
        );
    };

    useEffect(() => {
        if (params.id) {
            setSupplierId(params.id);
            getSupplierInfo(params.id).then((data) => {
                if (data) setSupplierInfo(data);
            });

            // Update global viewAsSupplier variable to the currently focused supplier.
            dispatch(setViewAsSupplier(params.id));
        }
    }, [dispatch, params.id, params.supplierTabName]);

    const tabPanelStyles = {
        height: 'calc(100% - 72px)',
        padding: '0',
    };

    return (
        <div data-testid="SupplierDetails" className={styles.container}>
            <div className={styles.viewingAs}>
                Viewing as {supplierInfo?.name}
                {'  '}
                <Typography className={styles.monospaced}>
                    ({supplierId})
                </Typography>
            </div>
            <Box
                sx={{
                    width: '100%',
                    typography: 'body1',
                    height: 'calc(100% - 24px)',
                    position: 'relative',
                }}
            >
                <TabContext value={value}>
                    <Box>
                        <TuTabList
                            onChange={handleChange}
                            aria-label="Supplier Details"
                            variant="scrollable"
                            scrollButtons="auto"
                        >
                            <TuTab
                                data-testid="AccountsTab"
                                label="Accounts"
                                value="accounts"
                                icon={<PropaneTank />}
                            />
                            <TuTab
                                data-testid="LiveReportingTab"
                                label="Live Reporting"
                                value="live-reporting"
                                icon={<Leaderboard />}
                            />
                            <TuTab
                                data-testid="EfficiencyTab"
                                label="Efficiency"
                                value="efficiency"
                                icon={<Insights />}
                            />
                            <TuTab
                                data-testid="PlanningTab"
                                label="Delivery Planning"
                                value="planning"
                                icon={<Today />}
                            />
                            <TuTab
                                data-testid="ConsumersTab"
                                label="Consumers"
                                value="consumers"
                                icon={<PeopleIcon />}
                            />
                            <TuTab
                                data-testid="IssuesTab"
                                label="Issues"
                                value="issues"
                                icon={<Assignment />}
                            />
                            <TuTab
                                data-testid="MonitorsTab"
                                label="Portfolio"
                                value="monitors"
                                icon={<Speed />}
                            />
                            <TuTab
                                data-testid="MapTab"
                                label="Map"
                                value="map"
                                icon={<Map />}
                            />
                            <TuTab
                                data-testid="TeamTab"
                                label="Team"
                                value="team"
                                icon={<PeopleIcon />}
                            />
                            <TuTab
                                data-testid="OrganizationTab"
                                label="Organization"
                                value="organization"
                                icon={<Business />}
                            />
                        </TuTabList>
                    </Box>
                    <TuTabPanel sx={{ ...tabPanelStyles }} value="accounts">
                        <Accounts viewAsSupplier={supplierId} />
                    </TuTabPanel>

                    <TuTabPanel
                        sx={{ ...tabPanelStyles }}
                        value="live-reporting"
                    >
                        <LiveMonitoring viewAsSupplier={supplierId} />
                    </TuTabPanel>

                    <TuTabPanel sx={{ ...tabPanelStyles }} value="efficiency">
                        <Efficiency viewAsSupplier={supplierId} />
                    </TuTabPanel>

                    <TuTabPanel sx={{ ...tabPanelStyles }} value="planning">
                        <Planning viewAsSupplier={supplierId} />
                    </TuTabPanel>

                    <TuTabPanel sx={{ ...tabPanelStyles }} value="consumers">
                        <Consumers viewAsSupplier={supplierId} />
                    </TuTabPanel>

                    <TuTabPanel sx={{ ...tabPanelStyles }} value="issues">
                        <Issues viewAsSupplier={supplierId} />
                    </TuTabPanel>

                    <TuTabPanel sx={{ ...tabPanelStyles }} value="monitors">
                        <Monitors viewAsSupplier={supplierId} />
                    </TuTabPanel>

                    <TuTabPanel sx={{ ...tabPanelStyles }} value="map">
                        <DeviceMap viewAsSupplier={supplierId} />
                    </TuTabPanel>

                    <TuTabPanel sx={{ ...tabPanelStyles }} value="team">
                        <Team viewAsSupplier={supplierId} />
                    </TuTabPanel>

                    <TuTabPanel sx={{ ...tabPanelStyles }} value="organization">
                        <Organization viewAsSupplier={supplierId} />
                    </TuTabPanel>
                </TabContext>
            </Box>
        </div>
    );
};
